import { forwardRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Camera, User, Maximize2, Minimize2, Circle } from "lucide-react";

/**
 * VideoPanel - Main video display area with local/remote video feeds
 */
const VideoPanel = forwardRef(({
  callState,
  callDuration,
  isRecording,
  isCameraOff,
  isFullscreen,
  selectedPatient,
  participants = [],
  onToggleFullscreen,
  localVideoRef,
  remoteVideoRef
}, ref) => {
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div 
      ref={ref}
      className={`relative bg-gray-900 rounded-lg overflow-hidden ${isFullscreen ? "fixed inset-0 z-50" : "aspect-video"}`}
    >
      {/* Remote Video / Main View */}
      {callState === "connected" ? (
        <>
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
          
          {/* Multi-participant grid overlay */}
          {participants.length > 0 && (
            <div className="absolute top-4 left-4 grid grid-cols-2 gap-2">
              {participants.filter(p => p.connected).map(participant => (
                <div 
                  key={participant.id}
                  className="w-32 h-24 bg-gray-800 rounded-lg overflow-hidden relative"
                >
                  {participant.stream ? (
                    <video
                      autoPlay
                      playsInline
                      ref={(el) => el && (el.srcObject = participant.stream)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-8 h-8 text-gray-500" />
                    </div>
                  )}
                  <Badge className="absolute bottom-1 left-1 text-xs">{participant.name}</Badge>
                </div>
              ))}
            </div>
          )}
        </>
      ) : callState === "calling" ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center mb-4 animate-pulse">
              <User className="w-12 h-12" />
            </div>
            <Circle className="absolute inset-0 w-24 h-24 text-primary animate-ping" />
          </div>
          <p className="text-lg font-medium">Calling {selectedPatient?.patient_name || selectedPatient?.name}...</p>
          <p className="text-sm text-gray-400 mt-2">Waiting for response</p>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white">
          <div className="text-center">
            <Camera className="w-16 h-16 mx-auto mb-4 text-gray-500" />
            <p className="text-gray-400">Select a patient to start a call</p>
          </div>
        </div>
      )}

      {/* Local Video (Picture-in-Picture) */}
      <div className="absolute bottom-4 right-4 w-48 h-36 bg-gray-800 rounded-lg overflow-hidden shadow-xl border-2 border-gray-700">
        {isCameraOff ? (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-10 h-10 text-gray-500" />
          </div>
        ) : (
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover mirror"
          />
        )}
        <Badge className="absolute bottom-2 left-2 text-xs">You</Badge>
      </div>

      {/* Call Status Overlay */}
      {callState !== "idle" && (
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <Badge variant={callState === "connected" ? "default" : "secondary"} className="bg-green-600">
            {callState === "calling" ? "Calling..." : `Connected • ${formatDuration(callDuration)}`}
          </Badge>
          {isRecording && (
            <Badge variant="destructive" className="text-xs animate-pulse">
              <Circle className="w-2 h-2 mr-1 fill-current" /> REC
            </Badge>
          )}
        </div>
      )}

      {/* Fullscreen Toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 text-white hover:bg-white/20"
        onClick={onToggleFullscreen}
      >
        {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
      </Button>
    </div>
  );
});

VideoPanel.displayName = "VideoPanel";
export default VideoPanel;
